import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Receipt, Calendar, MapPin, CreditCard, CheckCircle, XCircle } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import client from "@/Graphql/apolloClient";
import { INVOICE, UPDATESTATUSMUTATION, ECOMMERCE_CONFIG } from "@/Graphql/Queries";
import { CREATE_PAYMENT_INTENT } from "@/Graphql/Mutations";
import { generateMockData } from "@/Graphql/MockData";
import { useToast } from "@/hooks/use-toast";
import CheckoutForm from "@/components/CheckoutForm";
import MockCheckoutForm from "@/components/MockCheckoutForm";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from '@stripe/stripe-js';


interface PriceSummary {
  grossTotal?: number;
  subTotal: number;
  netTotal: number;
  tax: {
    percent: number;
    name: string;
    value: number;
  };
  currency: { code: string; symbol: string };
  couponName?: string;
  discountPercent?: number;
  discountedPrice?: number;
}

interface CampaignInventory {
  inventoryName: string | undefined;
  billboardId: string;
  billboardName: string;
  billboardType: string;
  inventoryPrice: number;
  inventoryThumbnailUrl: string;
  mediaOwnerId: string;
  mediaOwnerName: string;
  inventoryType: string;
  inventoryResolutions: string;
  inventoryReports: {
    totalPotentialViews: number;
    totalUniqueReach: number;
    averageFrequency: number;
    cpm: number;
  };
}

interface InvoiceData {
  id: string;
  name: string;
  dealId: string;
  startDate: { date: string };
  endDate: { date: string };
  summaryReport: {
    potentialViews: string;
    uniqueReach: string;
    averageFrequency: number;
  };
  priceSummary: PriceSummary;
  campaignInventories: CampaignInventory[];
  campaignStatus?: string; // Add campaign status to interface
}

interface CheckoutProps {
  base_path: string;
  yourLogo: string;
  currencyCode: string;
}

const validatePaymentConfig = (accessKey: string, secretKey: string) => {
  if (!accessKey || !secretKey) {
    throw new Error("Payment configuration is incomplete");
  }
  return true;
};

const validateInvoiceData = (invoice: InvoiceData) => {
  if (!invoice.id || !invoice.dealId || !invoice.priceSummary?.netTotal) {
    throw new Error("Invalid invoice data");
  }
  return true;
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = date.toLocaleDateString('en-GB', { month: 'short' }).replace('Sept', 'Sep');
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

// stripePromise must be initialized inside the component to access accessKey state

export default function Checkout({ base_path, yourLogo, currencyCode }: CheckoutProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [campaignData, setCampaignData] = useState<InvoiceData | null>(null);
  const [campaignStatus, setCampaignStatus] = useState<string>("PAYMENT_PENDING");
  const [showPaymentConfirmation, setShowPaymentConfirmation] = useState(false);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [showPaymentError, setShowPaymentError] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isHandlingPaymentReturn, setIsHandlingPaymentReturn] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [accessKey, setAccessKey] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [billboardCurrencyCode, setBillboardCurrencyCode] = useState(currencyCode);
  const [userdata, setUserdata] = useState<any[]>([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null);
  const [ecommerceConfigData, setEcommerceConfigData] = useState<any>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState<any>(null);

  const [clientSecret, setClientSecret] = useState('');
  const hasPaymentIntentRequestedRef = useRef(false);
  const isMockMode = import.meta.env.VITE_APP_MOCK_DATA === "true";
  const stripePromise = (!isMockMode && accessKey) ? loadStripe(accessKey) : null;

  // Authentication details
  const token = localStorage.getItem("authToken");
  const userId = localStorage.getItem("userId");
  const userEmail = localStorage.getItem("userEmail");
  const userAccountData = localStorage.getItem("userAccountData");
  const companyId = localStorage.getItem("companyId") || import.meta.env.VITE_APP_COMPANY_ID || "";

  const currentDomain = window.location.origin;

  const [paymentConfig, setPaymentConfig] = useState<any[]>([]);

  const fetchConfig = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_APP_FOLDER_PATH}/json/configJson.json?t=${new Date().getTime()}`);
      const data = await response.json();

      // Find the "Payment Options" page configuration
      const paymentPage = data.find((page: any) => page.name === "Payment Options");
      console.log("paymentPage --->", paymentPage);
      console.log("paymentPage.configurations --->", paymentPage.configurations);
      if (paymentPage && paymentPage.configurations) {
        setPaymentConfig(paymentPage.configurations);
      } else {
        console.warn("Payment Options configuration not found");
      }
    } catch (error) {
      console.error("Error fetching configuration:", error);
    }
  };

  // Call fetchConfig in a useEffect
  useEffect(() => {
    fetchConfig();
  }, []);

  // Helper function to check if a field is enabled
  const isFieldEnabled = (key: string, config: any[]) => {
    if (!config || !Array.isArray(config)) {
      return true;
    }

    // 1. Check top-level configurations first
    const fieldConfig = config.find(field => field?.key === key);

    // 2. If not found at top level, search inside nested 'fields' arrays
    let targetField = fieldConfig;
    if (!targetField) {
      for (const section of config) {
        if (section.fields && Array.isArray(section.fields)) {
          const nested = section.fields.find((f: any) => f.key === key);
          if (nested) {
            targetField = nested;
            break;
          }
        }
      }
    }

    // 3. If field still not found, default to true
    if (!targetField) {
      return true;
    }

    // 4. Handle specific types
    if (targetField.type === 'radio') {
      return targetField.default === 'yes';
    }

    if (targetField.type === 'switch') {
      return !!targetField.default;
    }

    return targetField?.default ?? true;
  };

  // Fetch ecommerce config using GraphQL
  useEffect(() => {
    const fetchEcommerceConfig = async () => {
      if (!token || !companyId) {
        setConfigLoading(false);
        return;
      }

      setConfigLoading(true);
      try {
        if (import.meta.env.VITE_APP_MOCK_DATA === "true") {
          const mockResponse: any = await generateMockData(ECOMMERCE_CONFIG);
          setEcommerceConfigData(mockResponse.data);
          setConfigError(null);
        } else {
          const { data } = await client.query({
            query: ECOMMERCE_CONFIG,
            variables: {
              accessToken: token,
              companyId: companyId
            },
            fetchPolicy: "network-only"
          });
          setEcommerceConfigData(data);
          setConfigError(null);
        }
      } catch (err) {
        setConfigError(err);
        setEcommerceConfigData(null);
      } finally {
        setConfigLoading(false);
      }
    };

    fetchEcommerceConfig();
  }, [token, companyId]);

  // Handle ecommerce config data from GraphQL
  useEffect(() => {
    if (ecommerceConfigData?.ecommerceConfig?.paymentIntegrationConfig) {
      const paymentConfig = ecommerceConfigData.ecommerceConfig.paymentIntegrationConfig;
      setAccessKey(paymentConfig.accessKey || "");
      // setSecretKey(paymentConfig.secretKey || ""); // Uncomment if secretKey is available in the response
    } else if (configError) {
      console.error("Error fetching ecommerce config:", configError);
      setErrorMessage("Error fetching payment configuration. Please try again later.");
    }
  }, [ecommerceConfigData, configError]);

  useEffect(() => {
    if (userAccountData) {
      const parsedUserAccountData = JSON.parse(userAccountData || "[]");
      console.log("print user account data-->", parsedUserAccountData);
      setUserdata(parsedUserAccountData);

    }
  }, [userAccountData]);

  // Handle return from Stripe payment (success or cancel)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const sessionId = urlParams.get('session_id');
    const paymentIntent = urlParams.get('payment_intent');
    const redirectStatus = urlParams.get('redirect_status');
    const campaignId = urlParams.get('campaign');
    const canceled = urlParams.get('canceled');

    // Only proceed if we have either a session_id (Checkout Session) or payment_intent (Payment Intent) indicating return from Stripe
    if ((sessionId || paymentIntent) && token && campaignId) {
      console.log('Processing Stripe redirect:', {
        sessionId,
        paymentIntent,
        redirectStatus,
        campaignId,
        canceled
      });
      setIsHandlingPaymentReturn(true);

      // Check if payment was canceled
      if (canceled === 'true' || redirectStatus === 'failed') {
        console.log('Payment was canceled or failed, refetching invoice data');
        setErrorMessage(null);
        setShowPaymentError(false);
        setLoading(false); // Ensure loading state is reset
        // Clean up URL parameters for cancel case
        navigate(`${base_path}/checkout?campaign=${campaignId}`, { replace: true });
        setIsHandlingPaymentReturn(false);
        return;
      }

      // Check if payment was successful
      if (canceled !== 'true' && (redirectStatus === 'succeeded' || sessionId)) {
        console.log('Payment successful, fetching updated campaign data');

        const handleUpdateStatus = async () => {
          try {
            let statusResponse;

            if (import.meta.env.VITE_APP_MOCK_DATA === "true") {
              // Use mock data
              const mockResponse: any = await generateMockData(UPDATESTATUSMUTATION);
              statusResponse = mockResponse;
            } else {
              // Use real GraphQL API
              statusResponse = await client.mutate({
                mutation: UPDATESTATUSMUTATION,
                variables: {
                  accessToken: token,
                  campaignId: campaignId,
                  reasonForRejection: "",
                  campaignStatus: "APPROVED",
                },
              });
            }

            if (statusResponse.data?.updateCampaignStatus?.success) {
              // Update campaign status to approved
              setCampaignStatus("APPROVED");

              // If we don't have campaign data yet, we need to fetch it for the success modal
              if (!campaignData) {
                try {
                  if (import.meta.env.VITE_APP_MOCK_DATA === "true") {
                    const mockResponse: any = await generateMockData(INVOICE);
                    const data = mockResponse.data;
                    setCampaignData(data.paymentInvoice);
                    setCampaignStatus("APPROVED"); // Override status to approved since payment was successful
                    setBillboardCurrencyCode(data.paymentInvoice?.priceSummary?.currency?.code || billboardCurrencyCode);
                  } else {
                    const { data } = await client.query({
                      query: INVOICE,
                      variables: {
                        accessToken: token,
                        campId: campaignId,
                      },
                      fetchPolicy: "network-only",
                    });

                    const invoiceData = data.paymentInvoice;
                    if (invoiceData) {
                      setCampaignData(invoiceData);
                      setCampaignStatus("APPROVED"); // Override status to approved since payment was successful
                      setBillboardCurrencyCode(invoiceData.priceSummary?.currency?.code || billboardCurrencyCode);
                    }
                  }
                } catch (fetchError) {
                  console.error("Error fetching campaign data for success modal:", fetchError);
                }
              }

              setShowPaymentSuccess(true);
              setLoading(false); // Ensure loading is false when showing success modal
              // Don't show toast here as the success modal will handle the user feedback
              // Clear query parameters to prevent re-triggering on refresh - do this after a small delay
              // setTimeout(() => {
              //   navigate(`${base_path}/checkout?campaiggggn=${campaignId}`, { replace: true });
              // }, 100);
            } else {
              throw new Error("Failed to update campaign status.");
            }
          } catch (error: any) {
            console.error("Status update error:", error);
            setErrorMessage(error.message || "Failed to update campaign status. Please try again.");

            toast({
              title: "Error",
              description: error.message || "Failed to update campaign status. Please try again.",
              variant: "destructive",
            });
            setShowPaymentError(true);
            navigate(`${base_path}/checkout?campaign=${campaignId}`, { replace: true });
          } finally {
            setIsHandlingPaymentReturn(false);
          }
        };
        handleUpdateStatus();
      } else if (paymentIntent && redirectStatus && redirectStatus !== 'succeeded') {
        // Handle other redirect statuses (like requires_action, requires_payment_method, etc.)
        console.log('Payment requires additional action or failed:', redirectStatus);
        setErrorMessage(`Payment ${redirectStatus.replace('_', ' ')}. Please try again.`);
        setShowPaymentError(true);
        setClientSecret('');
        hasPaymentIntentRequestedRef.current = false;
        navigate(`${base_path}/checkout?campaign=${campaignId}`, { replace: true });
        setIsHandlingPaymentReturn(false);
      }
    }
  }, [location.search, token, navigate, base_path, billboardCurrencyCode]);

  // Fetch invoice details
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const campaignId = urlParams.get('campaign');
    const sessionId = urlParams.get('session_id');
    const paymentIntent = urlParams.get('payment_intent');
    const redirectStatus = urlParams.get('redirect_status');
    const success = urlParams.get('success');

    // Don't fetch invoice data if we're returning from a successful payment,
    // if we're currently handling a payment return, or if the success modal is showing
    if ((sessionId && success === 'true') || (paymentIntent && redirectStatus === 'succeeded') || isHandlingPaymentReturn || showPaymentSuccess) {
      console.log('Skipping invoice fetch - payment success flow will handle, payment return in progress, or success modal showing');
      setLoading(false); // Set loading to false since we're not fetching
      return;
    }

    const fetchInvoiceDetails = async () => {
      setLoading(true);
      try {
        const { data } = await client.query({
          query: INVOICE,
          variables: {
            accessToken: token,
            campId: campaignId,
          },
          fetchPolicy: "network-only",
        });

        const invoiceData = data.paymentInvoice;
        if (invoiceData) {
          setCampaignData(invoiceData);
          setCampaignStatus(invoiceData.campaignStatus || "PAYMENT_PENDING");
          setBillboardCurrencyCode(invoiceData.priceSummary?.currency?.code || currencyCode);
        } else {
          setErrorMessage("Campaign not found.");
        }
      } catch (error) {
        console.error("Error fetching invoice details:", error);
        setErrorMessage("Failed to load campaign details.");
      } finally {
        setLoading(false);
      }
    };

    if (campaignId) {
      if (import.meta.env.VITE_APP_MOCK_DATA === "true") {
        generateMockData(INVOICE)
          .then((mockResponse: any) => {
            const data = mockResponse.data;
            setCampaignData(data.paymentInvoice);
            setCampaignStatus(data.paymentInvoice?.campaignStatus || "PAYMENT_PENDING");
            setBillboardCurrencyCode(data.paymentInvoice?.priceSummary?.currency?.code || currencyCode);
            setLoading(false);
          })
          .catch((err: any) => {
            console.error("Error generating mock data:", err);
            setErrorMessage("Failed to load mock data.");
            setLoading(false);
          });
      } else {
        fetchInvoiceDetails();
      }
    }
  }, [currencyCode, location.search, isHandlingPaymentReturn, showPaymentSuccess]);

  // Handle return from payment-method page
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const paymentMethod = urlParams.get('payment_method');
    const campaignId = urlParams.get('campaign');

    // Only trigger payment flow if user selected stripe and we don't have clientSecret yet
    if (paymentMethod === 'stripe' && !clientSecret && !hasPaymentIntentRequestedRef.current && campaignStatus !== "APPROVED" && campaignData) {
      setSelectedPaymentMethod('stripe');
      setShowPaymentConfirmation(true);
      setShowPaymentError(false);

      // Clean up URL to remove payment_method param after reading
      navigate(`${base_path}/checkout?campaign=${campaignId}`, { replace: true });
    }
  }, [location.search, clientSecret, campaignStatus, campaignData, navigate, base_path]);

  const formatCurrency = (amount: number) => {
    return `${billboardCurrencyCode} ${new Intl.NumberFormat('en-AU', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)}`;
  };

  const calculateDuration = (startDate: string, endDate: string): string => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return `${Math.ceil((end.getTime() - start.getTime()) / (1000 * 3600 * 24)) + 1} days`;
  };

  const handlePayNowClick = () => {
    // Navigate to payment method selection page
    navigate(`${base_path}/payment-method?campaign=${campaignData?.id}`);
  };

  const createPaymentIntent = async () => {
    try {
      let data;

      if (import.meta.env.VITE_APP_MOCK_DATA === "true") {
        // Use mock data
        const mockResponse: any = await generateMockData(CREATE_PAYMENT_INTENT);
        data = mockResponse.data;
      } else {
        // Use real GraphQL API
        const response = await client.mutate({
          mutation: CREATE_PAYMENT_INTENT,
          variables: {
            accessToken: token,
            dealId: campaignData?.dealId,
          },
        });
        data = response.data;
      }

      if (!data || !data.createPaymentIntent) {
        throw new Error("Failed to create payment intent");
      }

      if (data && data.createPaymentIntent?.code !== 200) {
        throw new Error(data.createPaymentIntent?.message || "Payment intent creation failed");
      }

      const _clientSecret = data.createPaymentIntent.clientSecret;
      if (!_clientSecret) {
        throw new Error("No client secret received");
      }

      setClientSecret(_clientSecret);
    } catch (error) {
      console.error("Error creating payment intent:", error);
      setShowPaymentError(true);
      setErrorMessage(error instanceof Error ? error.message : "Failed to initialize payment");
      hasPaymentIntentRequestedRef.current = false;
      throw error;
    }
  };

  const handleConfirmPayment = async () => {
    setShowPaymentConfirmation(false);

    // Prevent multiple calls, especially in React Strict Mode
    if (hasPaymentIntentRequestedRef.current) return;
    hasPaymentIntentRequestedRef.current = true;

    try {
      await createPaymentIntent();
    } catch (error) {
      console.error("Error creating payment intent:", error);
      setShowPaymentError(true);
      setErrorMessage("Failed to initialize payment. Please try again.");
      hasPaymentIntentRequestedRef.current = false;
    }
  };

  const handlePaymentSuccess = () => {
    setShowPaymentSuccess(false);
    toast({
      title: "Success",
      description: "Payment completed successfully!",
      variant: "default",
    });
    navigate(`${base_path}/payment-invoice?campaign=${campaignData?.id}`);
  };

  const handlePaymentSuccessClose = () => {
    setShowPaymentSuccess(false);
    // Just close the modal, don't navigate - user stays on checkout page with approved status
  };

  const handleMockPaymentSuccess = async () => {
    // Simulate payment success for mock mode
    try {
      const urlParams = new URLSearchParams(location.search);
      const campaignId = urlParams.get('campaign');

      if (!campaignId || !token) return;

      // Update campaign status to approved
      let statusResponse;

      if (import.meta.env.VITE_APP_MOCK_DATA === "true") {
        // Use mock data
        const mockResponse: any = await generateMockData(UPDATESTATUSMUTATION);
        statusResponse = mockResponse;
      } else {
        // Use real GraphQL API
        statusResponse = await client.mutate({
          mutation: UPDATESTATUSMUTATION,
          variables: {
            accessToken: token,
            campaignId: campaignId,
            reasonForRejection: "",
            campaignStatus: "APPROVED",
          },
        });
      }

      if (statusResponse.data?.updateCampaignStatus?.success) {
        setCampaignStatus("APPROVED");
        setShowPaymentSuccess(true);
        setClientSecret(''); // Clear client secret
        hasPaymentIntentRequestedRef.current = false;
      } else {
        throw new Error("Failed to update campaign status.");
      }
    } catch (error: any) {
      console.error("Mock payment error:", error);
      setErrorMessage(error.message || "Failed to process payment. Please try again.");
      setShowPaymentError(true);
    }
  };

  if (loading || configLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center">
            <div className="animate-spin w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <h1 className="text-2xl font-bold text-gray-900">Loading...</h1>
          </div>
        </div>
      </div>
    );
  }

  // Show processing screen when handling payment return but don't have campaign data yet
  if (isHandlingPaymentReturn && !campaignData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center">
            <div className="animate-spin w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <h1 className="text-2xl font-bold text-gray-900">Processing Payment...</h1>
            <p className="text-gray-600 mt-2">Please wait while we confirm your payment.</p>
          </div>
        </div>
      </div>
    );
  }

  // Show minimal layout when payment success modal is active - but still show the checkout page
  // The success modal will appear on top

  if (!campaignData || errorMessage) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900">Checkout Not Found</h1>
            <p className="text-gray-600 mt-2">{errorMessage || "The requested campaign checkout could not be found."}</p>
            <Button
              variant="outline"
              onClick={() => navigate(`${base_path}/my-campaigns`)}
              className="mt-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Campaigns
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const digitalBillboards = campaignData.campaignInventories?.filter(
    (inventory) => inventory.inventoryType === "digital"
  ) || [];
  const classicBillboards = campaignData.campaignInventories?.filter(
    (inventory) => inventory.inventoryType === "classic"
  ) || [];

  const paymentIntentOptions = {
    clientSecret: clientSecret,
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-[95vw] mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <div>
              <h1 className="text-3xl font-bold text-emerald-600">Checkout</h1>
              <p className="text-gray-600 mt-1">
                {campaignStatus === "APPROVED"
                  ? `Payment completed for ${campaignData.name}`
                  : `Complete your payment for Pending Payment Campaign`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {campaignStatus === "APPROVED" ? (
              <Badge variant="secondary" className="bg-green-100 text-green-800">
                Payment Approved
              </Badge>
            ) : (
              <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                Pending Payment
              </Badge>
            )}
            <Button
              variant="outline"
              onClick={() => navigate(`${base_path}/my-campaigns`)}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Campaigns
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-lg text-emerald-600">
                  Campaign Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Campaign Name</p>
                    <p className="text-sm font-medium text-gray-900 mt-1">{campaignData.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Campaign ID</p>
                    <p className="text-sm font-medium text-gray-900 mt-1">{campaignData.dealId}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Start Date</p>
                    <p className="text-sm font-medium text-gray-900 mt-1 flex items-center">
                      <Calendar className="h-4 w-4 mr-1 text-emerald-600" />
                      {formatDate(campaignData.startDate.date)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">End Date</p>
                    <p className="text-sm font-medium text-gray-900 mt-1 flex items-center">
                      <Calendar className="h-4 w-4 mr-1 text-emerald-600" />
                      {formatDate(campaignData.endDate.date)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Duration</p>
                    <p className="text-sm font-medium text-gray-900 mt-1">{calculateDuration(campaignData.startDate.date, campaignData.endDate.date)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                {campaignData?.campaignInventories?.length > 0 && (
                  <div className="bg-emerald-50 rounded-lg border border-emerald-200 overflow-hidden mt-3">
                    {/* Table Header */}
                    <div className="bg-emerald-600 text-white px-4 py-3">
                      <h4 className="font-semibold text-sm flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        Inventory Details
                      </h4>
                    </div>

                    {/* Table Content */}
                    <div className="bg-white max-h-[500px] overflow-y-auto">
                      {/* Digital Category */}
                      {campaignData.campaignInventories.some((inv: any) => inv.inventoryType?.toLowerCase().includes('digital')) && (
                        <>
                          <div className="bg-blue-50 px-4 py-2 border-l-4 border-blue-400">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-blue-900">
                                Digital ({campaignData.campaignInventories.filter((inv: any) => inv.inventoryType?.toLowerCase().includes('digital')).length} Digital{campaignData.campaignInventories.filter((inv: any) => inv.inventoryType?.toLowerCase().includes('digital')).length > 1 ? 's' : ''})
                              </span>
                            </div>
                          </div>
                          {campaignData.campaignInventories
                            .filter((inv: any) => inv.inventoryType?.toLowerCase().includes('digital'))
                            .map((inventory: any, idx: number) => (
                              <div key={`digital-${idx}`} className="flex items-center gap-4 p-4 border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
                                <img
                                  src={inventory.inventoryThumbnailUrl || `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`}
                                  alt={inventory.inventoryName}
                                  className="w-16 h-12 rounded object-cover border border-gray-200"
                                />
                                <div className="flex-1">
                                  <h5 className="text-sm font-medium text-gray-900 mb-1">{inventory.inventoryName}</h5>
                                  <Badge
                                    variant="secondary"
                                    className="bg-blue-100 text-blue-700 mb-1"
                                  >
                                    {inventory.inventoryType.charAt(0).toUpperCase() + inventory.inventoryType.slice(1).toLowerCase() === "Digital_billboard" ? "Digital" : inventory.inventoryType.charAt(0).toUpperCase() + inventory.inventoryType.slice(1).toLowerCase()}
                                  </Badge>
                                  <p className="text-xs text-emerald-600 hover:text-emerald-700 font-medium mb-1">By {inventory.mediaOwnerName}</p>
                                  <p className="text-xs text-gray-500">{inventory.inventoryReports?.totalPotentialViews?.toLocaleString() || 'N/A'} impressions</p>
                                  <p className="text-xs text-gray-500">Resolution: {inventory.inventoryResolutions || 'N/A'}</p>
                                </div>
                                <div className="text-right">
                                  <span className="text-sm font-medium text-emerald-600">
                                    {formatCurrency(inventory.inventoryPrice)}
                                  </span>
                                  <p className="text-xs text-gray-500">for {calculateDuration(campaignData.startDate.date, campaignData.endDate.date)}</p>
                                </div>
                              </div>
                            ))}
                        </>
                      )}

                      {/* Classic Category */}
                      {campaignData.campaignInventories.some((inv: any) => (inv.inventoryType?.toLowerCase().includes('static_billboard') || (!inv.inventoryType?.toLowerCase().includes('digital') && !inv.inventoryType?.toLowerCase().includes('package')))) && (
                        <>
                          <div className="bg-green-50 px-4 py-2 border-l-4 border-green-400">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-green-900">
                                Classic ({campaignData.campaignInventories.filter((inv: any) => (inv.inventoryType?.toLowerCase().includes('static_billboard') || (!inv.inventoryType?.toLowerCase().includes('digital') && !inv.inventoryType?.toLowerCase().includes('package')))).length} Classic)
                              </span>
                            </div>
                          </div>
                          {campaignData.campaignInventories
                            .filter((inv: any) => (inv.inventoryType?.toLowerCase().includes('static_billboard') || (!inv.inventoryType?.toLowerCase().includes('digital') && !inv.inventoryType?.toLowerCase().includes('package'))))
                            .map((inventory: any, idx: number) => (
                              <div key={`classic-${idx}`} className="flex items-center gap-4 p-4 border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
                                <img
                                  src={inventory.inventoryThumbnailUrl || `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`}
                                  alt={inventory.inventoryName}
                                  className="w-16 h-12 rounded object-cover border border-gray-200"
                                />
                                <div className="flex-1">
                                  <h5 className="text-sm font-medium text-gray-900 mb-1">{inventory.inventoryName}</h5>
                                  <Badge
                                    variant="secondary"
                                    className="bg-green-100 text-green-700 mb-1"
                                  >
                                    {inventory.inventoryType.charAt(0).toUpperCase() + inventory.inventoryType.slice(1).toLowerCase() === "Static_billboard" ? "Classic" : inventory.inventoryType.charAt(0).toUpperCase() + inventory.inventoryType.slice(1).toLowerCase()}
                                  </Badge>
                                  <p className="text-xs text-emerald-600 hover:text-emerald-700 font-medium mb-1">By {inventory.mediaOwnerName}</p>
                                  <p className="text-xs text-gray-500">{inventory.inventoryReports?.totalPotentialViews?.toLocaleString() || 'N/A'} impressions</p>
                                  <p className="text-xs text-gray-500">Resolution: {inventory.inventoryResolutions || 'N/A'}</p>
                                </div>
                                <div className="text-right">
                                  <span className="text-sm font-medium text-emerald-600">
                                    {formatCurrency(inventory.inventoryPrice)}
                                  </span>
                                  <p className="text-xs text-gray-500">for {calculateDuration(campaignData.startDate.date, campaignData.endDate.date)}</p>
                                </div>
                              </div>
                            ))}
                        </>
                      )}

                      {/* Package Category */}
                      {campaignData.campaignInventories.some((inv: any) => inv.inventoryType?.toLowerCase().includes('package')) && (
                        <>
                          <div className="bg-purple-50 px-4 py-2 border-l-4 border-purple-400">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-purple-900">
                                Package ({campaignData.campaignInventories.filter((inv: any) => inv.inventoryType?.toLowerCase().includes('package')).length} Package)
                              </span>
                            </div>
                          </div>
                          {campaignData.campaignInventories
                            .filter((inv: any) => inv.inventoryType?.toLowerCase().includes('package'))
                            .map((inventory: any, idx: number) => (
                              <div key={`package-${idx}`} className="flex items-center gap-4 p-4 border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
                                <img
                                  src={inventory.inventoryThumbnailUrl || `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`}
                                  alt={inventory.inventoryName}
                                  className="w-16 h-12 rounded object-cover border border-gray-200"
                                />
                                <div className="flex-1">
                                  <h5 className="text-sm font-medium text-gray-900 mb-1">{inventory.inventoryName}</h5>
                                  <Badge
                                    variant="secondary"
                                    className="bg-purple-100 text-purple-700 mb-1"
                                  >
                                    {inventory.inventoryType.charAt(0).toUpperCase() + inventory.inventoryType.slice(1).toLowerCase() === "Package" ? "Package" : inventory.inventoryType.charAt(0).toUpperCase() + inventory.inventoryType.slice(1).toLowerCase()}
                                  </Badge>
                                  <p className="text-xs text-emerald-600 hover:text-emerald-700 font-medium mb-1">By Multiple Media Owners</p>
                                  <p className="text-xs text-gray-500">{inventory.inventoryReports?.totalPotentialViews?.toLocaleString() || 'N/A'} impressions</p>
                                  <p className="text-xs text-gray-500">Resolution: {inventory.inventoryResolutions || 'N/A'}</p>
                                </div>
                                <div className="text-right">
                                  <span className="text-sm font-medium text-emerald-600">
                                    {formatCurrency(inventory.inventoryPrice)}
                                  </span>
                                  <p className="text-xs text-gray-500">for {calculateDuration(campaignData.startDate.date, campaignData.endDate.date)}</p>
                                </div>
                              </div>
                            ))}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            {/* Billing Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg text-emerald-600">Billing Details</CardTitle>
              </CardHeader>
              <CardContent>

                <div className="space-y-2">
                  {userdata[0]?.userCompanyName && (
                    <p className="text-gray-600">
                      Company Name: {userdata[0].userCompanyName}
                    </p>
                  )}
                  {userdata[0]?.email && (
                    <p className="text-gray-600">
                      Email: {userdata[0].email}
                    </p>
                  )}
                  {userdata[0]?.userCompanyAddress && (
                    <p className="text-gray-600">
                      Address: {userdata[0].userCompanyAddress}
                    </p>
                  )}
                  {userdata[0]?.userCity && (
                    <p className="text-gray-600">
                      State/Province: {userdata[0].userCity}
                    </p>
                  )}
                  {userdata[0]?.userCompanyState && (
                    <p className="text-gray-600">
                      State: {userdata[0].userCompanyState}
                    </p>
                  )}
                  {userdata[0]?.userCompanyPostcode && (
                    <p className="text-gray-600">
                      Postcode: {userdata[0].userCompanyPostcode}
                    </p>
                  )}
                  {userdata[0]?.userCountry && (
                    <p className="text-gray-600">
                      Country: {userdata[0].userCountry}
                    </p>
                  )}
                </div>

              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-lg text-emerald-600">
                  Payment Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Gross Total */}
                <div className="flex justify-between">
                  <span>Gross Total:</span>
                  <span>{formatCurrency(campaignData.priceSummary.grossTotal || 0)}</span>
                </div>

                {/* Discount Applied */}
                <div className="flex justify-between">
                  <span>
                    Discount Applied
                    {campaignData.priceSummary.discountPercent && campaignData.priceSummary.discountedPrice && campaignData.priceSummary.discountedPrice > 0
                      ? ` (${Math.round(campaignData.priceSummary.discountPercent)}%)`
                      : ''
                    }:
                  </span>
                  <span>
                    {campaignData.priceSummary.discountedPrice && campaignData.priceSummary.discountedPrice > 0
                      ? `- ${formatCurrency(campaignData.priceSummary.discountedPrice)}`
                      : formatCurrency(0)
                    }
                  </span>
                </div>

                {/* Subtotal (Excl. Tax) */}
                <div className="flex justify-between">
                  <span>Subtotal (Excl. {campaignData.priceSummary.tax.name}):</span>
                  <span>{formatCurrency(campaignData.priceSummary.subTotal)}</span>
                </div>

                {/* Tax */}
                <div className="flex justify-between">
                  <span>{campaignData.priceSummary.tax.name} ({Math.round(campaignData.priceSummary.tax.percent)}%):</span>
                  <span>{formatCurrency(campaignData.priceSummary.tax.value)}</span>
                </div>

                <Separator />

                {/* Grand Total */}
                <div className="flex justify-between text-lg font-semibold">
                  <span>Grand Total:</span>
                  <span className="text-emerald-600">{formatCurrency(campaignData.priceSummary.netTotal)}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg text-emerald-600">Payment Method</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* ONLY hide the Credit Card informational box if Stripe is disabled */}
                {isFieldEnabled('stripe_enabled', paymentConfig) && !clientSecret && (
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">Credit Card</span>
                      <div className="flex gap-2">
                        <div className="w-8 h-5 bg-blue-600 rounded text-white text-xs flex items-center justify-center">VISA</div>
                        <div className="w-8 h-5 bg-red-600 rounded text-white text-xs flex items-center justify-center">MC</div>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">
                      {campaignStatus === "APPROVED"
                        ? "Payment completed successfully"
                        : "Secure payment powered by Stripe"}
                    </p>
                  </div>
                )}

                {/* Show loading state when payment intent is being created (Stripe specific) */}
                {isFieldEnabled('stripe_enabled', paymentConfig) &&
                  campaignStatus !== "APPROVED" && hasPaymentIntentRequestedRef.current && !clientSecret && (
                    <div className="border rounded-lg p-4">
                      <div className="text-center">
                        <div className="animate-spin w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full mx-auto mb-2"></div>
                        <p className="text-sm text-gray-600">Preparing payment...</p>
                      </div>
                    </div>
                  )}

                {/* Stripe Checkout Form (Only if Stripe enabled) */}
                {isFieldEnabled('stripe_enabled', paymentConfig) &&
                  campaignStatus !== "APPROVED" && clientSecret && (
                    <div className="border rounded-lg p-4">
                      {isMockMode ? (
                        <MockCheckoutForm
                          totalAmount={formatCurrency(campaignData.priceSummary.netTotal)}
                          onPaymentSuccess={handleMockPaymentSuccess}
                        />
                      ) : stripePromise ? (
                        <Elements stripe={stripePromise} options={paymentIntentOptions}>
                          <CheckoutForm
                            totalAmount={formatCurrency(campaignData.priceSummary.netTotal)}
                            onPaymentError={(error) => {
                              setErrorMessage(error);
                              setShowPaymentError(true);
                            }}
                          />
                        </Elements>
                      ) : (
                        <div className="text-center text-gray-500 py-4">
                          Payment gateway not configured
                        </div>
                      )}
                    </div>
                  )}

                {/* BNPL Section (Only displayed if enabled in config) */}
                {isFieldEnabled('bnpl_enabled', paymentConfig) && !clientSecret && campaignStatus !== "APPROVED" && (
                  <div className="border rounded-lg p-4 bg-gray-50/50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">Buy Now Pay Later</span>
                      <Badge variant="outline" className="text-[10px] uppercase">Installments</Badge>
                    </div>
                    <p className="text-sm text-gray-600">Split your campaign cost into easy installments.</p>
                  </div>
                )}

                {/* Pay Now / Select Payment Method Button - ALWAYS visible if not approved */}
                {campaignStatus !== "APPROVED" && !clientSecret && !hasPaymentIntentRequestedRef.current && (
                  <Button
                    className="w-full btn-emerald-gradient"
                    size="lg"
                    onClick={handlePayNowClick}
                    disabled={isProcessingPayment}
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    Select Payment Method - {formatCurrency(campaignData.priceSummary.netTotal)}
                  </Button>
                )}

                {/* View Invoice button */}
                {campaignStatus === "APPROVED" && (
                  <Button
                    variant="outline"
                    className="w-full"
                    size="lg"
                    onClick={() => navigate(`${base_path}/payment-invoice?campaign=${campaignData?.id}`)}
                  >
                    <Receipt className="h-4 w-4 mr-2" />
                    View Invoice
                  </Button>
                )}

                {/* Footer Security/Instruction Text - Hidden if Stripe is false per request */}
                {campaignStatus === "APPROVED" || isFieldEnabled('stripe_enabled', paymentConfig) ? (
                  <div className="text-center">
                    <p className="text-xs text-gray-500">
                      {campaignStatus === "APPROVED"
                        ? "Payment has been processed successfully"
                        : "Your payment information is secure and encrypted"}
                    </p>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Dialog open={showPaymentConfirmation} onOpenChange={setShowPaymentConfirmation}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Payment confirmation</DialogTitle>
          </DialogHeader>
          <div className="text-center py-6">
            <Receipt className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <p className="text-lg mb-2">Are you sure you</p>
            <p className="text-lg mb-6">want to proceed with the payment?</p>
            <div className="flex gap-3 justify-center">
              <Button
                variant="outline"
                onClick={() => {
                  setShowPaymentConfirmation(false);
                  setClientSecret(''); // Reset client secret
                  hasPaymentIntentRequestedRef.current = false; // Reset ref
                }}
                className="px-8"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmPayment}
                className="bg-emerald-600 hover:bg-emerald-700 px-8"
              >
                Pay
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showPaymentSuccess} onOpenChange={handlePaymentSuccessClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Payment Successful</DialogTitle>
          </DialogHeader>
          <div className="text-center py-6">
            <div className="w-16 h-16 mx-auto mb-4 bg-emerald-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <p className="text-lg mb-2">Payment successful!</p>
            <p className="text-gray-600 mb-6">Your payment has been processed successfully.</p>
            <div className="flex gap-3 justify-center">
              {/* <Button
                variant="outline"
                onClick={handlePaymentSuccessClose}
                className="px-8"
              >
                Close
              </Button> */}
              <Button
                onClick={handlePaymentSuccess}
                className="bg-emerald-600 hover:bg-emerald-700 px-8"
              >
                View Invoice
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showPaymentError} onOpenChange={setShowPaymentError}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Payment Failed</DialogTitle>
          </DialogHeader>
          <div className="text-center py-6">
            <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <p className="text-lg mb-2">Payment failed</p>
            <p className="text-gray-600 mb-6">{errorMessage || "There was an error processing your payment. Please try again."}</p>
            <div className="flex gap-3 justify-center">
              <Button
                variant="outline"
                onClick={() => {
                  setShowPaymentError(false);
                  setClientSecret(''); // Reset client secret
                  hasPaymentIntentRequestedRef.current = false; // Reset ref
                }}
                className="px-8"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setShowPaymentError(false);
                  handlePayNowClick(); // Restart the payment process
                }}
                className="bg-emerald-600 hover:bg-emerald-700 px-8"
              >
                Try Again
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}